import * as React from "react";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import loadRecaptchaScript from "./ReCAPTCHALoader";

export type ReCAPTCHAAction =
   "data_removal_request" |
   "password_reset" |
   "share_link" |
   "send_message_to_host" |
   "text_application" |
   "enterprise_demo_request" |
   "payment_add" |
   "waitlist_request" |
   "affiliate_user_invite";

interface ReCAPTCHAProps {
   sitekey: string;
   action: ReCAPTCHAAction;
   onChange: (token: string | null) => void;
   onExpired?: () => void;
   size?: "normal" | "compact";
}

export interface ReCAPTCHARef {
   reset(): void;
}

const ReCAPTCHA = React.forwardRef<ReCAPTCHARef, ReCAPTCHAProps & { grecaptcha: any }>(({ grecaptcha, sitekey, action, onChange, onExpired, size }, ref) => {
   const wrapperRef = useRef<HTMLDivElement>(null);
   const widgetIdRef = useRef<number | null>(null);
   const onChangeRef = useRef(onChange);
   const onExpiredRef = useRef(onExpired);

   useEffect(() => {
      onChangeRef.current = onChange;
   }, [onChange]);

   useEffect(() => {
      onExpiredRef.current = onExpired;
   }, [onExpired]);

   useEffect(() => {
      if (!wrapperRef.current) return;

      widgetIdRef.current = grecaptcha.enterprise.render(wrapperRef.current, {
         sitekey,
         action,
         size,
         callback: (token) => onChangeRef.current(token),
         "expired-callback": () => onExpiredRef.current?.()
      });

      return () => {
         if (widgetIdRef.current !== null) {
            grecaptcha.enterprise.reset(widgetIdRef.current);
            widgetIdRef.current = null;
         }
      };
   }, [grecaptcha, sitekey, action, size]);

   useImperativeHandle(ref, () => ({
      reset() {
         if (widgetIdRef.current !== null) {
            grecaptcha.enterprise.reset(widgetIdRef.current);
         }
      }
   }), [grecaptcha]);


   return <div ref={wrapperRef}></div>
});

const ReCAPTCHAWrapper = React.forwardRef<ReCAPTCHARef, ReCAPTCHAProps>((props, ref) => {
   const [isScriptLoaded, setIsScriptLoaded] = useState(false);

   useEffect(() => {
      loadRecaptchaScript().then(() => {
         setIsScriptLoaded(true);
      });
   }, []);

   return isScriptLoaded
      ? <ReCAPTCHA ref={ref} {...props} grecaptcha={window["grecaptcha"]} />
      : null;
});

export default ReCAPTCHAWrapper;
